import { Component, ElementRef, Injector, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudEditorParentService, CrudEditorSelfService, onValueChangeEvent, requiredIfValidator, UserStorage } from '@scisys-uim/uim-common';
import { Asset, ASSET_ASPECT, ASSET_STATUS_DISPOSED, ASSET_TYPE_TYPE, AssetType, BusinessUnit } from 'assets-model';
import { AssetService, AssetTagsService, AssetTypeService, BusinessUnitService, COMPOSED_ASSET_CLASS_CODE, CostCentreService, ElectricalAspectService, MaintenanceAspectService, MechanicalAspectService, RefCodeStorage, TransferAssetService } from 'assets-service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AutocompleteStringFilterFunctionAsync } from '../../../shared/form/autocomplete.util';
import { FormCustomiser } from '../../../shared/form/form-customiser';
import { ElectricalAspectItemComponent } from '../../aspects/editor/electrical-aspect-item.component';
import { MaintenanceAspectItemComponent } from '../../aspects/editor/maintenance-aspect-item.component';
import { MechanicalAspectItemComponent } from '../../aspects/editor/mechanical-aspect-item.component';
import { TransferAssetDialogComponent } from '../../transferasset/dialogs/transfer-asset-dialog.component';
import { AssetItemComponentBase } from './asset-item.component.base';

export const ASSET_ITEM_INPUT_FORM_DISPLAY_DEFAULT: string[][] = [
  //row1
  ['assetType', 'businessUnit', 'costCentre', 'location', 'description'],
  //row2
  ['identityReference', 'manufacturer', 'model', 'purchaseDate', 'purchasePrice', 'status'],
  //row3
  ['tags'],
  //row4
  ['comments'],
  //row5
  ['updatedCreatedStats'],
  //row6
  ['aspects']
]


export const ASSET_ITEM_INPUT_FORM_DISPLAY_CREATE_MODE_DISABLED: string[] = ['createdBy', 'createdDate', 'updateBy', 'updatedDate', 'updatedCreatedStats'];

export function assetItemDefaultFormExcluding(excluding: string[]): string[][] {
  var formDisplay = [];
  for (let group of ASSET_ITEM_INPUT_FORM_DISPLAY_DEFAULT) {
    formDisplay.push(group.filter(f => !excluding.includes(f)));
  }
  return formDisplay
}

@Component({
  selector: 'app-asset-item',
  templateUrl: './asset-item.component.html',
  styleUrls: ['./asset-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class AssetItemComponent extends AssetItemComponentBase {

  private _assetControlComponents: AssetControlComponent[] = [];
  registerControlComponent(cmp: AssetControlComponent) {
    this._assetControlComponents.push(cmp);
  }
  deregisterControlComponent(cmp: AssetControlComponent) {
    this._assetControlComponents = this._assetControlComponents.filter(e => e != cmp);
  }

  @ViewChild('assetTypeTemplate') assetTypeTemplate: TemplateRef<any>;
  @ViewChild('businessUnitTemplate') businessUnitTemplate: TemplateRef<any>;
  @ViewChild('costCentreTemplate') costCentreTemplate: TemplateRef<any>;
  @ViewChild('locationTemplate') locationTemplate: TemplateRef<any>;
  @ViewChild('descriptionTemplate') descriptionTemplate: TemplateRef<any>;
  @ViewChild('identityReferenceTemplate') identityReferenceTemplate!: TemplateRef<any>;
  @ViewChild('manufacturerTemplate') manufacturerTemplate: TemplateRef<any>;
  @ViewChild('modelTemplate') modelTemplate: TemplateRef<any>;
  @ViewChild('purchaseDateTemplate') purchaseDateTemplate: TemplateRef<any>;
  @ViewChild('purchasePriceTemplate') purchasePriceTemplate: TemplateRef<any>;
  @ViewChild('commentsTemplate') commentsTemplate: TemplateRef<any>;
  @ViewChild('tagsTemplate') tagsTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('createdByTemplate') createdByTemplate: TemplateRef<any>;
  @ViewChild('createdDateTemplate') createdDateTemplate: TemplateRef<any>;
  @ViewChild('updateByTemplate') updateByTemplate: TemplateRef<any>;
  @ViewChild('updatedDateTemplate') updatedDateTemplate: TemplateRef<any>;
  @ViewChild('aspectsTemplate') aspectsTemplate: TemplateRef<any>;
  @ViewChild('updatedCreatedStatsTemplate') updatedCreatedStatsTemplate: TemplateRef<any>;

  private _inputFormDisplay: string[][] = ASSET_ITEM_INPUT_FORM_DISPLAY_DEFAULT;
  private inputFormTemplateMap: { [key: string]: TemplateRef<any> } = {};
  private _disabledFilters: string[] = [];

  get inputFormDisplay() {
    return this._inputFormDisplay;
  }
  @Input()
  set inputFormDisplay(value: string[][]) {
    if (value) {
      this._inputFormDisplay = this.filterInputFormDisplay(value);
    } else {
      this._inputFormDisplay = [];
    }
  }

  get disabledFilters() {
    if (this._disabledFilters == null) {
      this._disabledFilters = [];
    }
    return this._disabledFilters;
  }
  @Input()
  set disabledFilters(value: string[]) {
    this._disabledFilters = value;
    this.inputFormDisplay = this.inputFormDisplay;
  }

  locationDefaultDebounceMillis: number = 500;
  @ViewChild('location') locationInput: ElementRef;
  private locationSelectionListSubject: Subject<string[]> = new BehaviorSubject([]);
  locationSelectionList: Observable<string[]> = this.locationSelectionListSubject.asObservable();

  manufacturerDefaultDebounceMillis: number = 500;
  @ViewChild('manufacturer') manufacturerInput: ElementRef;
  private manufacturerSelectionListSubject: Subject<string[]> = new BehaviorSubject([]);
  manufacturerSelectionList: Observable<string[]> = this.manufacturerSelectionListSubject.asObservable();

  modelDefaultDebounceMillis: number = 500;
  @ViewChild('model') modelInput: ElementRef;
  private modelSelectionListSubject: Subject<string[]> = new BehaviorSubject([]);
  modelSelectionList: Observable<string[]> = this.modelSelectionListSubject.asObservable();

  isTransferAssetPending: boolean = false;

  @Input()
  initialFormCustomiser: FormCustomiser;

  @Input()
  fieldDefaultsFormsCustomiser: FormCustomiser;


  @Input('composedAssetClass') composedAssetClassCode: COMPOSED_ASSET_CLASS_CODE = "Asset";

  @Input()
  locationSelectionListFilterLoad: AutocompleteStringFilterFunctionAsync = (text: string) => {
    return this.assetService.findExistingAttributeValues('location', text, this.item?.assetType?.name, true);
  }

  @Input()
  manufacturerSelectionListFilterLoad: AutocompleteStringFilterFunctionAsync = (text: string) => {
    return this.assetService.findExistingAttributeValues('manufacturer', text, this.item?.assetType?.name, true);
  }

  @Input()
  modelSelectionListFilterLoad: AutocompleteStringFilterFunctionAsync = (text: string) => {
    return this.assetService.findExistingAttributeValues('model', text, this.item?.assetType?.name, true);
  }

  constructor(protected assetService: AssetService,
    protected assetTypeService: AssetTypeService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected transferAssetService: TransferAssetService,
    protected electricalAspectService: ElectricalAspectService,
    protected maintenanceAspectService: MaintenanceAspectService,
    protected mechanicalAspectService: MechanicalAspectService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(assetService, assetTypeService, businessUnitService, costCentreService, electricalAspectService, maintenanceAspectService, mechanicalAspectService, refCodeStorage, fb, elementRef, router, route, userStorage, dialog, injector, snackbar);
  }

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.composedAssetClassLoadSelectionList();
    this.businessUnitLoadSelectionList();
    this.assetTypeLoadSelectionList();
    this.statusLoadSelectionList();

    this.addAllWarnings();
  }

  addAllWarnings(): void {
    super.addAllWarnings();
    this.addWarningDynamic(() => "Maintence Info is not allowed for this Asset Type", () => this.item?.assetType?.maintenanceAspectRequirement == 'NOT_ALLOWED' && this.item.maintenanceAspect != null);
    this.addWarningDynamic(() => "Maintence Info is required for this Asset Type", () => this.item?.assetType?.maintenanceAspectRequirement == 'REQUIRED' && this.item.maintenanceAspect == null);
    this.addWarningDynamic(() => "Electrical Info is not allowed for this Asset Type", () => this.item?.assetType?.electricalAspectRequirement == 'NOT_ALLOWED' && this.item.electricalAspect != null);
    this.addWarningDynamic(() => "Electrical Info is required for this Asset Type", () => this.item?.assetType?.electricalAspectRequirement == 'REQUIRED' && this.item.electricalAspect == null);
    this.addWarningDynamic(() => "Mechanical Info is not allowed for this Asset Type", () => this.item?.assetType?.mechanicalAspectRequirement == 'NOT_ALLOWED' && this.item.mechanicalAspect != null);
    this.addWarningDynamic(() => "Mechanical Info is required for this Asset Type", () => this.item?.assetType?.mechanicalAspectRequirement == 'REQUIRED' && this.item.mechanicalAspect == null);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.wireLocationAutocomplete();
    this.wireManufacturerAutocomplete();
    this.wireModelAutocomplete();
    this.loadInputFormTemplateMap();
  }

  wireLocationAutocomplete(): void {
    if (this.location) {
      onValueChangeEvent(this.location, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.location = null;
          }
        }
        this.locationSelectionListFilter(value);
      }, {
        debounceMillis: this.locationDefaultDebounceMillis
      });
    }
  }
  locationSelectionListFilter(text: string) {
    this.locationSelectionListFilterLoad(text).subscribe(items => {
      this.locationSelectionListSubject.next(items);
    });
  }


  wireManufacturerAutocomplete(): void {
    if (this.manufacturer) {
      onValueChangeEvent(this.manufacturer, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.manufacturer = null;
          }
        }
        this.manufacturerSelectionListFilter(value);
      }, {
        debounceMillis: this.manufacturerDefaultDebounceMillis
      });
    }
  }
  manufacturerSelectionListFilter(text: string) {
    this.manufacturerSelectionListFilterLoad(text).subscribe(items => {
      this.manufacturerSelectionListSubject.next(items);
    });
  }

  wireModelAutocomplete(): void {
    if (this.model) {
      onValueChangeEvent(this.model, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.model = null;
          }
        }
        this.modelSelectionListFilter(value);
      }, {
        debounceMillis: this.modelDefaultDebounceMillis
      });
    }
  }
  modelSelectionListFilter(text: string) {
    this.modelSelectionListFilterLoad(text).subscribe(items => {
      this.modelSelectionListSubject.next(items);
    });
  }

  createForm(): FormGroup {
    const formGroup = super.createForm();
    formGroup.addControl('tags', this.fb.control(null))
    formGroup.controls.composedAssetClass.clearValidators();
    //can't find a way to add to existing validators?
    formGroup.setValidators([
      requiredIfValidator(() => this.comments, () => this.item?.status == ASSET_STATUS_DISPOSED, "Comments", { required: "Comments are required when disposing assets." }),
    ]);

    if (this.initialFormCustomiser != null) {
      this.initialFormCustomiser(formGroup);
    }

    return formGroup;
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }

    this.assetId?.disable();
    this.composedAssetClass?.disable()
    this.createdDate?.disable();
    this.createdBy?.disable();
    this.updatedDate?.disable();
    this.updatedBy?.disable();
    //only allow changing of asset type if in create mode or asset type not yet set
    this.assetType?.disable();
    if (this.createMode || this.item.assetType == null) {
      this.assetType?.enable();
    }

    this.setIsTransferAssetPendingForApproval();

    if (this.fieldDefaultsFormsCustomiser != null) {
      this.fieldDefaultsFormsCustomiser(this.form);
    }
  }

  itemSet() {
    super.itemSet();
    this.locationSelectionListFilter(null);
    this.modelSelectionListFilter(null);
    this.manufacturerSelectionListFilter(null);
    if (this.initialFormCustomiser != null) {
      this.initialFormCustomiser(this.form);
    }
  }

  isTransferAssetEnabled() {
    return !this.isTransferAssetPending;
  }

  launchTransferAssetDialog() {
    this.dialog.open(TransferAssetDialogComponent, {
      panelClass: 'dialog-resizable',
      width: '600px',
      data: { asset: this.item }
    }).afterClosed().subscribe(
      result => {
        this.setDefaultDisabledFields();
      });
  }

  assetTypeDisplay(item: AssetType) {
    if (item == null) {
      return "Other";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE_TYPE.description(item);
  }

  assetTypeChangedByUser(value: AssetType, unsetDependents?: boolean): void {
    super.assetTypeChangedByUser(value, unsetDependents);
    if (value != null && typeof value != 'string') {
      this.item.initAspectsForAssetType(this.createMode);
    }
    if (typeof value != 'string') {
      this.locationSelectionListFilter(null);
      this.modelSelectionListFilter(null);
      this.manufacturerSelectionListFilter(null);
    }
    this.loadInputFormTemplateMap();
  }

  statusChangedByUser(value: string, unsetDependents?: boolean): void {
    super.statusChangedByUser(value, unsetDependents);
    if (value == ASSET_STATUS_DISPOSED) {
      this.item.comments = null;
      this.comments.markAsTouched();
    }
    this.form.updateValueAndValidity();
  }

  costCentreLoadSelectionList() {
    if (this.item.businessUnit != null) {
      this.costCentreService.findAllByBusinessUnit(this.item.businessUnit?.buId).subscribe(
        data => {
          this.costCentreSelectionList = data.items;
          if (!this.readOnly) {
            this.costCentre?.enable();
          }
        }
      );
    } else {
      this.costCentreSelectionList = [];
      this.costCentre?.disable();
    }
  }

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    super.businessUnitChanged(value, unsetDependents);
    if (value == null || value?.buId != this.item.costCentre?.businessUnit?.buId) {
      this.item.costCentre = null;
    }
    this.costCentreLoadSelectionList();

  }

  setIsTransferAssetPendingForApproval() {
    var filters = { assetId: this.item.assetId, approved: null }
    this.transferAssetService.findByFullFilters(filters).subscribe(
      data => {
        if (data.items && data.items.filter(e => e.approvedRejectedDate == null && e.approvedRejectedBy == null).length > 0)
          this.isTransferAssetPending = true;
      });
  }

  addDisabledFilter(filter: string) {
    if (!this.isDisabledFilter(filter)) {
      this.disabledFilters.push(filter);
    }
  }

  isDisabledFilter(filter: string): boolean {
    return this.disabledFilters.includes(filter);
  }

  isShowFilter(filter: string): boolean {
    return !this.disabledFilters.includes(filter);
  }

  templateNameFromInputFormName(inputName: string): string {
    const sfx = 'Template';
    return inputName + sfx;
  }

  loadInputFormTemplateMap() {
    for (let inputFormGroup of this.inputFormDisplay) {
      for (let inputForm of inputFormGroup) {
        const templateName = this.templateNameFromInputFormName(inputForm)
        this.inputFormTemplateMap[templateName] = this[templateName];
      }
    }
  }

  filterInputFormDisplay(filterDisplay: string[][]): string[][] {
    var cleaned = [];
    for (let filterGroup of filterDisplay) {
      if (filterGroup.length <= 0) {
        continue;
      }
      var cleanedGroup = [];
      for (let filter of filterGroup) {
        if (!this.isShowFilter(filter) ||
          (this.createMode && ASSET_ITEM_INPUT_FORM_DISPLAY_CREATE_MODE_DISABLED.includes(filter))) {
          continue;
        }
        cleanedGroup.push(filter);
      }
      cleaned.push(cleanedGroup);
    }
    return cleaned
  }

  getInputFormTemplate(inputName: string) {
    return this.inputFormTemplateMap[this.templateNameFromInputFormName(inputName)];
  }

  isEmptyInputFormGroup(inputFormGroup: string[]): boolean {
    if (inputFormGroup == null || inputFormGroup.length <= 0) {
      return true;
    } else {
      var templateFound = false;
      for (let input of inputFormGroup) {
        if (this.getInputFormTemplate(input) != null) {
          templateFound = true;
          break;
        }
      }
      return !templateFound;
    }
  }

  markFormsTouched(): void {
    super.markFormsTouched();
    for (let cmp of this._assetControlComponents) {
      cmp.markFormsTouched();
    }
  }

  isPersistenceEnabled(): boolean {
    var enabled = super.isPersistenceEnabled();
    for (let cmp of this._assetControlComponents) {
      enabled = enabled && cmp.isPersistenceEnabled()
    }
    return enabled;
  }

  getFormErrorList() {
    var result = super.getFormErrorList();
    for (let cmp of this._assetControlComponents) {
      result.push(...cmp.getFormErrorList());
    }
    return result;

  }
}

@Component({
  selector: 'app-asset-control',
  template: ``,
})
export abstract class AssetControlComponent {
  @Input()
  item: Asset;

  @Input()
  createMode: boolean;

  @Input()
  readOnly: boolean;

  _parentAssetComponent: AssetItemComponent;
  @Input()
  set parentAssetComponent(cmp: AssetItemComponent) {
    if (cmp) {
      cmp?.deregisterControlComponent(this);
      cmp?.registerControlComponent(this);
    } else {
      this._parentAssetComponent?.deregisterControlComponent(this);
    }
    this._parentAssetComponent = cmp;
  }
  get parentAssetComponent() {
    return this._parentAssetComponent;
  }

  constructor() { }

  ngOnDestroy() {
    this.parentAssetComponent.deregisterControlComponent(this);
  }

  abstract markFormsTouched();

  abstract isPersistenceEnabled();

  abstract getFormErrorList();

}

@Component({
  selector: 'app-asset-tags-control',
  templateUrl: './asset-tags-control.component.html',
  styleUrls: ['./asset-item.component.scss'],
})
export class AssetTagsControlComponent extends AssetControlComponent {

  @Input('composedAssetClass') composedAssetClassCode: COMPOSED_ASSET_CLASS_CODE = "Asset";

  tagInputField: string;
  existingTagList: string[];

  @ViewChild('tags') tagsInput: ElementRef;
  private tagsSelectionListSubject: Subject<string[]> = new BehaviorSubject([]);
  tagsSelectionList: Observable<String[]> = this.tagsSelectionListSubject.asObservable();
  @ViewChild('tags', { read: MatAutocompleteTrigger }) tagsAutocompleteTrigger: MatAutocompleteTrigger;

  get fullTagList() {
    return Array.from(new Set([...this.existingTagList, ...this.item.tags]));
  }

  form: FormGroup;

  constructor(protected assetTagsService: AssetTagsService,
    protected snackbar: MatSnackBar,
    protected fb: FormBuilder) {
    super();
    this.form = this.fb.group({
      tags: [null]
    });
  }

  ngOnInit() {
    this.tagsLoadSelectionList();
  }

  ngAfterViewInit(): void {
    this.wireTagsAutocomplete();
  }

  wireTagsAutocomplete(): void {
    if (this.tags) {
      onValueChangeEvent(this.tags, value => {
        if (value == "") {
          value = null;
          this.tagInputField = null;
        }
        this.tagsSelectionListFilter(value);
      }, {
        debounceMillis: 100
      });
    }
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      event.preventDefault();
    }
  }

  selectedTagClick(event: MatAutocompleteSelectedEvent): void {
    this.selectedTag(event.option.value);
  }

  selectedTag(tag: string): void {
    tag = this.assetTagsService.sanitizeTag(tag);
    if (this.item.tags) {
      this.item.tags.push(tag);
    } else {
      this.item.tags = [tag];
    }
    this.tagsInput.nativeElement.value = '';
    this.tags.setValue(null);
    this.tagsSelectionListFilter(null);
  }

  tagsLoadSelectionList() {
    this.assetTagsService.findAllTagsForAssetClass(this.composedAssetClassCode).subscribe(
      data => {
        this.existingTagList = data.sort();
        this.tagsSelectionListFilter(null);
      }
    );
  }

  selectedTagEnter() {
    if (this.isNewTag(this.tagInputField) || this.isMatchingUnusedExistingTag(this.tagInputField)) {
      this.selectedTag(this.tagInputField);
    } else {
      const sanitizedTag = this.assetTagsService.sanitizeTag(this.tagInputField);
      if (sanitizedTag != null) {
        var message = "Tag '" + sanitizedTag + "' already exists."
        if (this.tagInputField != sanitizedTag) {
          message = "Tag '" + this.tagInputField + "' already exists as '" + sanitizedTag + "'";
        }
        this.snackbar.open(message, "OK", { duration: 800, verticalPosition: 'top' });
      }
    }
  }

  isNewTag(text: string) {
    const sanitizedTag = this.assetTagsService.sanitizeTag(text);
    return sanitizedTag != null && !this.fullTagList.includes(sanitizedTag);
  }

  isMatchingUnusedExistingTag(text: string) {
    const sanitizedTag = this.assetTagsService.sanitizeTag(text);
    return this.existingTagList.includes(sanitizedTag) && !this.item.tags.includes(sanitizedTag);
  }

  tagsSelectionListFilter(text: string): void {
    var filtered = this.existingTagList ? this.existingTagList.filter(p => !(this.item.tags || []).find(o => o == p)) : [];
    if (text != null && text.length > 0) {
      filtered = filtered.filter(p => this.assetTagsService.matchingTagPartial(text, p));
    }
    this.tagsSelectionListSubject.next(filtered);
  }

  removeTag(tag: string): void {
    const index = this.item.tags.indexOf(tag);

    if (index >= 0) {
      this.item.tags.splice(index, 1);
      this.tagsSelectionListFilter(null);
    }
  }


  tagsChanging(value: string[], unsetDependents: boolean = true) { }

  tagsChanged(value: string[], unsetDependents: boolean = true) { }

  markFormsTouched() {
    this.form.markAllAsTouched();
  }
  isPersistenceEnabled() {
    return this.form.valid;
  }
  getFormErrorList() {
    //implement this if we ever add validation on tags
    return [];
  }

  get tags() {
    return this.form?.get('tags');
  }

}

@Component({
  selector: 'app-asset-aspects-control',
  templateUrl: './asset-aspects-control.component.html',
  styleUrls: ['./asset-item.component.scss'],
})
export class AssetAspectsControlComponent extends AssetControlComponent {

  @ViewChild(MaintenanceAspectItemComponent) maintenanceAspectItemComponent: MaintenanceAspectItemComponent;
  @ViewChild(ElectricalAspectItemComponent) electricalAspectItemComponent: ElectricalAspectItemComponent;
  @ViewChild(MechanicalAspectItemComponent) mechanicalAspectItemComponent: MechanicalAspectItemComponent;

  constructor() {
    super();
  }

  isAspectAllowed(aspect: ASSET_ASPECT): boolean {
    if (this.item?.assetType == null) {
      return true;
    }
    if (aspect == 'ElectricalAspect') {
      return this.item.assetType?.electricalAspectRequirement != 'NOT_ALLOWED';
    }
    if (aspect == 'MechanicalAspect') {
      return this.item.assetType?.mechanicalAspectRequirement != 'NOT_ALLOWED';
    }
    if (aspect == 'MaintenanceAspect') {
      return this.item.assetType?.maintenanceAspectRequirement != 'NOT_ALLOWED';
    }
  }
  isAspectRequired(aspect: ASSET_ASPECT): boolean {
    if (this.item?.assetType == null) {
      return false;
    }
    if (aspect == 'ElectricalAspect') {
      return this.item.assetType?.electricalAspectRequirement == 'REQUIRED';
    }
    if (aspect == 'MechanicalAspect') {
      return this.item.assetType?.mechanicalAspectRequirement == 'REQUIRED';
    }
    if (aspect == 'MaintenanceAspect') {
      return this.item.assetType?.maintenanceAspectRequirement == 'REQUIRED';
    }
  }

  addAspect(aspect: ASSET_ASPECT) {
    var msg = "Add " + aspect + " Aspect. (This is a debug message)";
    if (aspect == 'ElectricalAspect') {
      msg = "If this is an electrical asset, you can add information around PAT testing and other info.";
    } else if (aspect == 'MechanicalAspect') {
      msg = "If this is an mechanical asset, you can add information around WBV/HAVS and other info.";
    } else if (aspect == 'MaintenanceAspect') {
      msg = "If this asset requires formal maintenance, you can add information around the types of maintenance and other info."
    }
    msg = msg + " Are you sure you want to proceed?"
    var title = "Add " + aspect.replace("Aspect", "") + " Information?"
    this.parentAssetComponent.openSimpleConfirmationDialog(msg, title, "Confirm").afterClosed().subscribe(
      confirm => {
        if (confirm) {
          this.item.createAspect(aspect, false);
          this.maybeDefaultAspectInfoFromAssetType(aspect);
        }
      }
    )
  }

  removeAspect(aspect: ASSET_ASPECT) {
    this.parentAssetComponent.openSimpleConfirmationDialog("Are you sure you want to remove any " + aspect.replace("Aspect", "") + " information?", "Remove Info", "Confirm").afterClosed().subscribe(
      confirm => {
        if (confirm) {
          this.item.removeAspect(aspect);
        }
      }
    )
  }

  maybeDefaultAspectInfoFromAssetType(aspect: ASSET_ASPECT) {
    if (this.item?.assetType != null) {
      if (aspect == 'MaintenanceAspect') {
        this.parentAssetComponent.openSimpleConfirmationDialog("Would you like to set the defaults based on asset type '" + this.item.assetType.name + "'?", "Set Defaults", "Yes", "No").afterClosed().subscribe(
          confirm => {
            if (confirm) {
              this.item.setDefaultsForAspects(aspect);
            }
          }
        )
      }
    }
  }

  markFormsTouched() {
    if (this.electricalAspectItemComponent) {
      this.electricalAspectItemComponent.markFormsTouched();
    }
    if (this.mechanicalAspectItemComponent) {
      this.mechanicalAspectItemComponent.markFormsTouched();
    }
    if (this.maintenanceAspectItemComponent) {
      this.maintenanceAspectItemComponent.markFormsTouched();
    }
  }

  isPersistenceEnabled() {
    return (!this.electricalAspectItemComponent || this.electricalAspectItemComponent.isPersistenceEnabled()) &&
      (!this.mechanicalAspectItemComponent || this.mechanicalAspectItemComponent.isPersistenceEnabled()) &&
      (!this.maintenanceAspectItemComponent || this.maintenanceAspectItemComponent.isPersistenceEnabled());
  }

  getFormErrorList() {
    var result = [];
    if (this.electricalAspectItemComponent) {
      result.push(...this.electricalAspectItemComponent.getFormErrorList());
    }
    if (this.mechanicalAspectItemComponent) {
      result.push(...this.mechanicalAspectItemComponent.getFormErrorList());
    }
    if (this.maintenanceAspectItemComponent) {
      result.push(...this.maintenanceAspectItemComponent.getFormErrorList());
    }
    return result;
  }
}

@Component({
  selector: 'app-asset-additional-info-control',
  templateUrl: './asset-additional-info-control.component.html',
  styleUrls: ['./asset-item.component.scss'],
})
export class AssetAdditionalInfoControlComponent extends AssetControlComponent {

  form: FormGroup;

  constructor(protected assetTagsService: AssetTagsService,
    protected snackbar: MatSnackBar,
    protected fb: FormBuilder) {
    super();
    this.form = this.fb.group({
      createdBy: [null],
      createdDate: [null],
      updatedBy: [null],
      updatedDate: [null],
    });
    this.form.disable();
  }

  markFormsTouched() {

  }

  isPersistenceEnabled() {
    return true;
  }

  getFormErrorList() {
    var result = [];
    return result;
  }

}

